import { ChevronLeftIcon } from 'lucide-react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';

import { useMe } from '@/platform/auth/use-me';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { ADMIN_ROOT, platformAdminLinks, type PlatformAdminLink } from '@/platform/lib/admin';
import { cn } from '@/lib/utils';

/**
 * `/verwaltung/*`: sub-navigation on the left (desktop), as a scrollable bar (tablet).
 * Phones reach the sections through Profil → Verwaltung and get a back link instead.
 */
export function AdminLayout() {
  const copy = useVoiceCopy();
  const me = useMe();
  const location = useLocation();

  if (!me.data) {
    return <Outlet />;
  }

  const links = platformAdminLinks(me.data);
  if (links.length === 0) {
    return (
      <section className="mx-auto max-w-md space-y-3 text-center">
        <h1 className="text-2xl font-bold">Kein Zugriff</h1>
        <p className="text-muted-foreground">{copy('platform.access.admin.denied')}</p>
      </section>
    );
  }

  const onOverview = location.pathname === ADMIN_ROOT;

  return (
    <div className="lg:grid lg:grid-cols-[13rem_minmax(0,1fr)] lg:gap-8">
      <aside className="hidden lg:block" aria-label="Verwaltung">
        <p className="mb-2 px-3 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
          <Link to={ADMIN_ROOT} className="hover:text-foreground">
            Verwaltung
          </Link>
        </p>
        <AdminNavList links={links} orientation="vertical" />
      </aside>

      <nav
        aria-label="Verwaltung"
        className="-mx-4 mb-6 hidden overflow-x-auto overflow-y-hidden border-b border-border px-4 sm:block lg:hidden"
      >
        <AdminNavList links={links} orientation="horizontal" />
      </nav>

      {onOverview ? null : (
        <Link
          to={ADMIN_ROOT}
          className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground sm:hidden"
        >
          <ChevronLeftIcon className="size-4" aria-hidden />
          Verwaltung
        </Link>
      )}

      <div className="min-w-0">
        <Outlet />
      </div>
    </div>
  );
}

function AdminNavList({
  links,
  orientation,
}: {
  links: PlatformAdminLink[];
  orientation: 'vertical' | 'horizontal';
}) {
  const vertical = orientation === 'vertical';
  return (
    <ul className={cn(vertical ? 'space-y-0.5' : 'flex gap-1 whitespace-nowrap')}>
      {links.map((link) => (
        <li key={link.id}>
          <NavLink
            to={link.to}
            className={({ isActive }) =>
              cn(
                'block text-sm transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                vertical
                  ? 'rounded-md px-3 py-2 hover:bg-accent'
                  : '-mb-px border-b-2 border-transparent px-3 py-2.5 hover:text-foreground',
                isActive
                  ? vertical
                    ? 'bg-accent font-medium text-foreground'
                    : 'border-primary font-medium text-foreground'
                  : 'text-muted-foreground',
              )
            }
          >
            {link.label}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}
